import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import AppShell from "./AppShell";
import axios from "axios";
import {
  Plus, Search, FileText, Send, Check, X,
  ArrowRight, Copy, Trash2, ExternalLink
} from "lucide-react";

const statusColors = {
  draft: "text-[#6B7280] bg-[#F3F4F6] border-[#E5E7EB]",
  sent: "text-[#D97706] bg-[#FEF3C7] border-[#FDE68A]",
  accepted: "text-[#059669] bg-[#ECFDF5] border-[#A7F3D0]",
  declined: "text-[#DC2626] bg-[#FEF2F2] border-[#FECACA]",
  converted: "text-[#1A3DFF] bg-[#EFF6FF] border-[#BFDBFE]",
  expired: "text-[#9CA3AF] bg-[#F9FAFB] border-[#E5E7EB]",
};

const Estimates = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => { fetchEstimates(); }, []);

  const fetchEstimates = async () => {
    try {
      const { data } = await axios.get(`${API}/estimates`);
      setEstimates(data);
    } catch (err) {
      console.error("Failed to fetch estimates:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this estimate?")) return;
    try {
      await axios.delete(`${API}/estimates/${id}`);
      fetchEstimates();
    } catch (err) { alert("Failed to delete"); }
  };

  const handleConvert = async (id) => {
    try {
      const { data } = await axios.post(`${API}/estimates/${id}/convert`);
      navigate(`/invoices/${data.id}`);
    } catch (err) { alert(err.response?.data?.detail || "Failed to convert"); }
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/estimate/${token}`);
  };

  const formatCurrency = (amount, currency = "USD") =>
    new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount || 0);

  const filtered = estimates.filter(e => {
    if (filter !== "all" && e.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (e.estimate_number?.toLowerCase().includes(q) || e.client?.name?.toLowerCase().includes(q));
    }
    return true;
  });

  const counts = { all: estimates.length, draft: 0, sent: 0, accepted: 0, declined: 0, converted: 0 };
  estimates.forEach(e => { if (counts[e.status] !== undefined) counts[e.status]++; });

  return (
    <AppShell active="estimates">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-[22px] sm:text-[28px] tracking-[-0.03em] text-[#0A0A0A]">Estimates</h1>
          <p className="text-[13px] sm:text-[14px] text-[#6B7280] mt-1">Send quotes, convert accepted ones to invoices</p>
        </div>
        <Link to="/estimates/new" className="btn-primary text-[13px] px-4 py-2.5 flex items-center gap-2 w-fit" data-testid="new-estimate-btn">
          <Plus className="w-4 h-4" /> New estimate
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
          <input type="text" placeholder="Search estimates..." value={search} onChange={e => setSearch(e.target.value)} className="input pl-10 text-[14px] w-full" data-testid="estimate-search" />
        </div>
        <div className="flex gap-1 bg-[#F3F4F6] p-1 w-fit" data-testid="estimate-filters">
          {["all", "draft", "sent", "accepted", "declined"].map(f => (
            <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 text-[12px] font-medium capitalize transition-colors ${filter === f ? "bg-white shadow-sm text-[#0A0A0A]" : "text-[#6B7280] hover:text-[#0A0A0A]"}`} data-testid={`filter-${f}`}>
              {f} {counts[f] > 0 ? `(${counts[f]})` : ""}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center"><p className="text-[14px] text-[#6B7280]">Loading estimates...</p></div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center bg-white border border-[#E5E7EB]">
          <FileText className="w-8 h-8 text-[#D1D5DB] mx-auto mb-3" />
          <p className="text-[15px] text-[#6B7280] mb-1">{search || filter !== "all" ? "No estimates match your filters" : "No estimates yet"}</p>
          <p className="text-[13px] text-[#9CA3AF] mb-4">Create your first estimate to send a quote to a client.</p>
          <Link to="/estimates/new" className="btn-primary text-[13px] px-4 py-2 inline-flex items-center gap-2" data-testid="empty-new-estimate-btn">
            <Plus className="w-4 h-4" /> New estimate
          </Link>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] overflow-hidden" data-testid="estimates-table">
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold text-[11px] text-[#9CA3AF] uppercase tracking-wide">Estimate</th>
                  <th className="text-left px-5 py-3 font-semibold text-[11px] text-[#9CA3AF] uppercase tracking-wide">Client</th>
                  <th className="text-left px-5 py-3 font-semibold text-[11px] text-[#9CA3AF] uppercase tracking-wide">Status</th>
                  <th className="text-right px-5 py-3 font-semibold text-[11px] text-[#9CA3AF] uppercase tracking-wide">Amount</th>
                  <th className="text-right px-5 py-3 font-semibold text-[11px] text-[#9CA3AF] uppercase tracking-wide">Valid Until</th>
                  <th className="px-5 py-3 w-32"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(est => (
                  <tr key={est.id} className="border-b border-[#E5E7EB] hover:bg-[#F9FAFB] cursor-pointer" onClick={() => navigate(`/estimates/${est.id}/edit`)} data-testid={`estimate-row-${est.id}`}>
                    <td className="px-5 py-4">
                      <span className="font-mono font-semibold text-[14px] text-[#0A0A0A]">{est.estimate_number}</span>
                      <p className="text-[12px] text-[#9CA3AF] mt-0.5">{est.issue_date}</p>
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-[14px] text-[#0A0A0A]">{est.client?.name || "\u2014"}</span>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`text-[11px] font-bold uppercase tracking-[0.05em] px-2 py-0.5 border ${statusColors[est.status] || statusColors.draft}`}>
                        {est.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right font-mono font-semibold text-[14px] text-[#0A0A0A]">
                      {formatCurrency(est.total, est.currency)}
                    </td>
                    <td className="px-5 py-4 text-right text-[13px] text-[#6B7280]">{est.valid_until}</td>
                    <td className="px-5 py-4" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {est.status === "accepted" && (
                          <button onClick={() => handleConvert(est.id)} className="p-1.5 hover:bg-[#ECFDF5] text-[#059669]" title="Convert to invoice" data-testid={`convert-${est.id}`}>
                            <ArrowRight className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button onClick={() => copyLink(est.share_token)} className="p-1.5 hover:bg-[#F3F4F6] text-[#6B7280]" title="Copy link" data-testid={`copy-link-${est.id}`}>
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handleDelete(est.id)} className="p-1.5 hover:bg-[#FEF2F2] text-[#9CA3AF] hover:text-[#EF4444]" title="Delete" data-testid={`delete-${est.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile */}
          <div className="sm:hidden divide-y divide-[#E5E7EB]">
            {filtered.map(est => (
              <div key={est.id} className="p-4" onClick={() => navigate(`/estimates/${est.id}/edit`)} data-testid={`estimate-mobile-${est.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono font-semibold text-[14px] text-[#0A0A0A]">{est.estimate_number}</span>
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 border ${statusColors[est.status] || statusColors.draft}`}>{est.status}</span>
                </div>
                <p className="text-[13px] text-[#6B7280]">{est.client?.name || "\u2014"}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="font-mono font-semibold text-[14px] text-[#0A0A0A]">{formatCurrency(est.total, est.currency)}</span>
                  <span className="text-[12px] text-[#9CA3AF]">{est.valid_until}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </AppShell>
  );
};

export default Estimates;
